import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Building2Icon, 
  SearchIcon, 
  ChevronRightIcon, 
  LoaderIcon,
  LogOutIcon,
  DownloadIcon
} from 'lucide-react';
import { thingsboardService } from '../services/thingsboard';
import { useTranslation } from 'react-i18next';

interface Unit {
  id: {
    id: string;
    entityType: 'ASSET';
  };
  name: string;
  label?: string;
  type: string;
}

interface UnitSelectionScreenProps {
  onSelect: (assetId: string, assetName: string) => void;
  onLogout: () => void;
  isMobile?: boolean;
}

export function UnitSelectionScreen({ onSelect, onLogout, isMobile = false }: UnitSelectionScreenProps) {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>('');
  const { t } = useTranslation();

  useEffect(() => {
    async function fetchUnits() {
      try {
        setLoading(true);
        // First try to get the user scope to see if we should fetch tenant or customer assets
        const user = await thingsboardService.getUser();
        
        // Extract user name (Last Name + First Name for Korean style: 홍길동)
        if (user) {
          const fullName = `${user.lastName || ''}${user.firstName || ''}`;
          setUserName(fullName || user.email || '');
        }
        
        let assetData;
        if (user.authority === 'TENANT_ADMIN') {
          assetData = await thingsboardService.getAssetsByType('호');
        } else if (user.customerId && user.customerId.id !== '13814000-1dd2-11b2-8080-808080808080') {
          // If customer user, fetch customer assets
          assetData = await thingsboardService.getCustomerAssetsByType(user.customerId.id, '호');
        } else {
          // Fallback to tenant assets if no customerId or something else
          assetData = await thingsboardService.getAssetsByType('호');
        }

        if (assetData && assetData.data) {
          setUnits(assetData.data);
        } else {
          setError(t('app.errorLoadingSpaces', '공간 목록을 불러올 수 없습니다.'));
        }
      } catch (err) {
        console.error('Failed to fetch ho units:', err);
        setError(t('common.errorLoadingData', '데이터를 불러오는 중 오류가 발생했습니다.'));
      } finally {
        setLoading(false);
      }
    }

    fetchUnits();
  }, []);

  const filteredUnits = units.filter(unit => 
    (unit.label || unit.name).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={`min-h-screen flex flex-col ${isMobile ? '' : 'items-center justify-center p-8'}`} style={{ background: 'var(--bg-space)' }}>
      {/* Background decoration */}
      <div className="fixed inset-0 pointer-events-none opacity-5">
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.1) 1px, transparent 0)',
            backgroundSize: '32px 32px'
          }}
        />
      </div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`relative z-10 w-full ${isMobile ? 'flex-1 flex flex-col px-6 py-10' : 'max-w-4xl glass-panel rounded-3xl p-10'}`}
      >
        {/* Header */}
        <div className={`flex flex-col ${isMobile ? 'mb-8' : 'mb-12'}`}>
          <div className="flex items-center justify-between mb-2">
            <motion.img 
              src="/remove_background_fr.png" 
              alt="Samsung" 
              className="w-28 h-auto opacity-60"
            />
            <button 
              onClick={onLogout}
              className="p-2 text-slate-500 hover:text-rose-400 transition-colors"
              title={t('common.logout', '로그아웃')}
            >
              <LogOutIcon className="w-5 h-5" />
            </button>
          </div>
          <h1 className={`${isMobile ? 'text-lg' : 'text-xl'} font-bold tracking-tight`} style={{ color: 'var(--mobile-text-primary)' }}>
            {t('app.spaceSelectionTitle', '공간 선택')}
          </h1>
          <p className="text-sm mt-2" style={{ color: 'var(--mobile-text-secondary)' }}>
            {t('app.spaceSelectionDesc', '관리할 공간을 선택해주세요')}
          </p>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4.5 h-4.5" style={{ color: 'var(--mobile-text-muted)' }} />
          <input 
            type="text"
            placeholder={t('app.searchSpacePlaceholder', '공간명 검색...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-12 border rounded-xl pl-11 pr-4 text-sm focus:outline-none focus:border-cyan-500/50 transition-all placeholder:opacity-50"
            style={{ background: 'var(--mobile-bg-input)', borderColor: 'var(--mobile-border-strong)', color: 'var(--mobile-text-primary)' }}
          />
        </div>

        {/* List Content */}
        <div className={`flex-1 min-h-0 ${isMobile ? 'overflow-y-auto' : ''}`}>
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div 
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center justify-center py-20"
              >
                <LoaderIcon className="w-8 h-8 text-cyan-500 animate-spin mb-4" />
                <span className="font-medium tracking-widest text-xs uppercase" style={{ color: 'var(--mobile-text-muted)' }}>{t('app.requestingSpaceInfo', '공간정보 요청 중...')}</span>
              </motion.div>
            ) : error ? (
              <motion.div 
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-20"
              >
                <div className="w-12 h-12 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-4 text-rose-500">
                  !
                </div>
                <p className="font-medium" style={{ color: 'var(--mobile-text-primary)' }}>{error}</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="mt-4 text-sm text-cyan-500 hover:text-cyan-400"
                >
                  {t('common.refresh', '새로고침')}
                </button>
              </motion.div>
            ) : (
              <motion.div 
                key="list"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`grid ${isMobile ? 'grid-cols-1 gap-3' : 'grid-cols-2 gap-4'}`}
              >
                {filteredUnits.length > 0 ? (
                  filteredUnits.map((unit, idx) => (
                    <motion.button
                      key={unit.id.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.03 }}
                      onClick={() => onSelect(unit.id.id, unit.label || unit.name)}
                      className="group flex items-center gap-4 p-4 rounded-2xl border hover:opacity-90 transition-all text-left"
                      style={{ background: 'var(--mobile-bg-card)', borderColor: 'var(--mobile-border)' }}
                    >
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center transition-all" style={{ background: 'var(--mobile-chip-active-bg)', color: 'var(--mobile-chip-active-text)' }}>
                        <Building2Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate" style={{ color: 'var(--mobile-text-primary)' }}>
                          {unit.label || unit.name}
                        </div>

                      </div>
                      <ChevronRightIcon className="w-4 h-4 transition-colors" style={{ color: 'var(--mobile-text-hint)' }} />
                    </motion.button>
                  ))
                ) : (
                  <div className="col-span-full text-center py-20 text-sm" style={{ color: 'var(--mobile-text-muted)' }}>
                    {t('app.noSearchResults', '검색 결과가 없습니다.')}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* App Download Link - Mobile Only */}
        {isMobile && (
          <div className="mt-8 flex justify-center">
            <a 
              href="https://sed.seqsys.net/sed.apk" 
              className="flex items-center gap-2.5 px-6 py-3 rounded-2xl bg-cyan-500/10 text-cyan-500 text-sm font-bold hover:bg-cyan-500/20 transition-all border border-cyan-500/20 shadow-sm"
            >
              <DownloadIcon className="w-4.5 h-4.5" />
              <span>{t('app.downloadAndroidApp', '안드로이드용 앱 다운로드')}</span>
            </a>
          </div>
        )}

        {/* Footer */}
        <div className={`mt-8 pt-6 border-t ${isMobile ? 'text-center' : 'flex items-center justify-between'}`} style={{ borderColor: 'var(--mobile-border)' }}>
          <span className="text-[0.8rem] font-mono hidden md:block" style={{ color: 'var(--mobile-text-muted)' }}>
            로그인 사용자 : {userName}
          </span>
          <span className="text-[0.8rem] font-mono uppercase tracking-widest mx-auto md:mx-0" style={{ color: 'var(--mobile-text-muted)' }}>
            SAMSUNG C&T · SED Dashboard
          </span>
        </div>
      </motion.div>
    </div>
  );
}
